<template functional>
  <!--
  ==============================================================================
  When empty
  ==============================================================================
  -->
  <div
    v-if="!props.value || props.value.length < 1"
    :class="[data.class, data.staticClass]"
    :style="[data.style, data.staticStyle]">
    <slot name="before-results"></slot>
    <slot name="empty">
      <div class="p-2">{{ parent.$t("common.noItems") }}</div>
    </slot>
  </div>
  <!--
  ==============================================================================
  When non empty
  ==============================================================================
  -->
  <div
    v-else
    class="table d-flex flex-column"
    :class="[data.class, data.staticClass, {
       'table-striped': props.striped,
       'table-hover': props.hover,
       'table-bordered': props.bordered,
       'table-condensed': props.condensed,
    }]"
    :style="[data.style, data.staticStyle]">
    <!--
    ============================================================================
    Header
    ============================================================================
    -->
    <div class="thead" v-if="!props.stacked && !props.noHeader">
      <div class="row mx-0">
        <!--
        ========================================================================
        Header index
        ========================================================================
        -->
        <slot name="header(index)" v-if="props.showIndex">
          <div class="font-weight-bold col">
            {{ parent.$t("common.index") }}
          </div>
        </slot>
        <!--
        ========================================================================
        Header
        ========================================================================
        -->
        <template v-for="(col, c) in $options.visibleColumns(props.columns)">
          <slot
            :name="'header(' + col.key + ')'"
            :col="col"
            :c="c">
            <component
              v-if="col.showHeader === undefined || col.showHeader"
              :is="$options.headerComponent"
              :sortedBy="props.sortedBy"
              :sortOrder="props.sortOrder"
              :stacked="props.stacked"
              :col="col"
              :key="c"
              @sort="$options.handleSort(listeners, ...arguments)"
            />
          </slot>
        </template>
      </div>
    </div>
    <!--
    ============================================================================
    Body
    ============================================================================
    -->
    <div class="tbody flex-grow-1" :class="{
      'p-relative': props.anchored
    }">
      <div :class="{
        'anchored scrollable': props.anchored
      }">
        <slot name="before-results"></slot>
        <template v-for="(row, r) in props.value">
          <div class="row mx-0" :key="r">
            <slot
              name="before-item"
              :row="row"
              :r="r"
            ></slot>
            <!--
            ====================================================================
            Row index
            ====================================================================
            -->
            <template v-if="props.showIndex">
              <slot name="'header(index)'" v-if="props.stacked">
                <div class="font-weight-bold col text-right">
                  {{ parent.$t("common.index") }}
                </div>
              </slot>
              <slot
                name="cell(index)"
                :row="row"
                :r="r">
                <div class="col">{{ r }}</div>
              </slot>
              <div v-if="props.stacked" class="w-100 p-0"></div>
            </template>
            <!--
            ====================================================================
            Row
            ====================================================================
            -->
            <template v-for="(col, c) in $options.visibleColumns(props.columns)">
              <slot
                :name="'header(' + col.key + ')'"
                :col="col"
                :c="c">
                <component
                  :is="$options.headerComponent"
                  v-if="props.stacked"
                  :sortedBy="props.sortedBy"
                  :sortOrder="props.sortOrder"
                  :stacked="props.stacked"
                  :col="col"
                  :key="c + '_s'"
                  @sort="$options.handleSort(listeners, ...arguments)"
                />
              </slot>
              <slot
                :name="'cell(' + col.key + ')'"
                :row="row"
                :r="r"
                :col="col"
                :c="c">
                <component
                  :is="$options.componentFor(props, col)"
                  :class="$options.classFor(props, row, col)"
                  :style="$options.styleFor(props, row, col)"
                  :disabled="$options.disabledFor(props, row, col)"
                  :key="c"
                  :row="row"
                  :col="col"
                  :r="r"
                  :c="c"
                  :value="$options.valueFor({ row, col, r, c })"
                  :error="$options.errorFor({ row, col, r, c })"
                  v-bind="$options.propsFor({ row, col, r, c })"
                  @input="$options.inputFor(listeners, { row, col, r, c, value: $event })"
                ></component>
              </slot>
              <div v-if="props.stacked" class="w-100 p-0" :key="c + '_nl'"></div>
            </template>
            <slot
              name="after-item"
              :row="row"
              :r="r"
            ></slot>
          </div>
        </template>
      </div>
    </div>
    <!--
    ============================================================================
    Footer
    ============================================================================
    -->
    <div class="tfoot" v-if="!props.stacked">
      <div class="row mx-0">
        <template v-for="(col, c) in $options.visibleColumns(props.columns)">
          <slot
            :name="'footer(' + col.key + ')'"
            :col="col"
            :c="c"
          ></slot>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import DataTableCell from './DataTableCell';
import DataTableActionsCell from './DataTableActionsCell';
import DataTableBooleanCell from './DataTableBooleanCell';
import DataTableProgressCell from './DataTableProgressCell';
import DataTableTagsCell from './DataTableTagsCell';
import DataTableHeader from './DataTableHeader';
import { resolveFn } from 'core/utils';

const CELL_TYPES = {
  'actions': DataTableActionsCell,
  'boolean': DataTableBooleanCell,
  'progress': DataTableProgressCell,
  'tags': DataTableTagsCell,
};

export default {
  functional: true,

  props: {
    anchored: {
      type: Boolean,
      default: true,
    },

    bordered: {
      type: Boolean,
      default: false
    },

    condensed: {
      type: Boolean,
      default: false
    },

    columns: {
      type: Array,
      default: () => []
    },

    disabled: {
      type: Boolean,
      default: false
    },

    hover: {
      type: Boolean,
      default: false
    },

    noHeader: {
      type: Boolean,
      default: false
    },

    showIndex: {
      type: Boolean,
      default: false
    },

    sortedBy: {
      type: String,
      default: undefined
    },

    sortOrder: {
      type: String,
      default: undefined
    },

    stacked: {
      type: Boolean,
      default: false
    },

    striped: {
      type: Boolean,
      default: false
    },

    value: {
      type: Array,
      default: () => []
    }
  },

  headerComponent: DataTableHeader,

  visibleColumns(columns) {
    return columns.filter(x => x.show === undefined || resolveFn(x.show));
  },

  componentFor(props, col) {
    if (col.component) {
      return col.component;
    }

    if (col.type) {
      const component = CELL_TYPES[col.type];
      if (component) {
        return component;
      } else {
        console.error("Could not find component for " + col.type);
      }
    }

    return DataTableCell;
  },

  classFor(props, row, col) {
    if (props.stacked) {
      return resolveFn(col.stackedStyleClass, { row, col }) || 'col';
    }
    return resolveFn(col.styleClass, { row, col }) || 'col';
  },

  styleFor(props, row, col) {
    if (props.stacked) {
      return undefined;
    }
    return resolveFn(col.style, { row, col });
  },

  disabledFor(props, row, col) {
    const colDisabled = col === undefined || col.disabled === undefined
        ? false
        : resolveFn(col.disabled, { row, col });
    return colDisabled || props.disabled;
  },

  valueFor({ row, col, r, c }) {
    if (col.value) {
      return col.value({ row, col, r, c });
    }

    return row[col.key];
  },

  errorFor({ row, col, r, c }) {
    if (col.error) {
      return col.error({ row, col, r, c });
    }
  },

  propsFor({ row, col, r, c }) {
    if (!col.component) {
      return undefined;
    }

    return resolveFn(col.props, { row, col, r, c });
  },

  inputFor(listeners, { row, col, r, c, value }) {
    if (col.input) {
      return col.input({ row, col, r, c, value });
    }

    if (listeners && listeners.input) {
      listeners.input({ row, col, r, c, value });
    }
  },

  handleSort(listeners, ...args) {
    if (listeners && listeners.sort) {
      listeners.sort(...args);
    }
  }
};
</script>
